import React from "react";
import { Box, Button, HStack, Text } from "@chakra-ui/react";

import { capturePostHog } from "#app/utils/posthog.client.js";

/**
 * An inline hint under the date pickers when the picked days look like a
 * common mistake, with a one-tap fix and a way to say the pick was meant.
 * It never blocks submitting.
 *
 * Records request_form_date_hint_shown / _fixed / _dismissed with `kind`,
 * so how often each hint fires and gets used can be read from the funnel.
 */
export function DatePickHint({
  kind,
  analyticsProps,
  title,
  children,
  fixLabel,
  onFix,
  keepLabel,
  onKeep,
}) {
  React.useEffect(() => {
    capturePostHog("request_form_date_hint_shown", { ...analyticsProps, kind });
  }, [kind]);

  const handleFix = () => {
    capturePostHog("request_form_date_hint_fixed", { ...analyticsProps, kind });
    onFix();
  };

  const handleKeep = () => {
    capturePostHog("request_form_date_hint_dismissed", {
      ...analyticsProps,
      kind,
    });
    onKeep();
  };

  return (
    <Box
      borderWidth="1px"
      borderColor="border"
      borderLeftWidth="3px"
      borderLeftColor="accent"
      borderRadius="md"
      px={4}
      py={3}
      data-testid="date-pick-hint"
      data-kind={kind}
    >
      <Text fontSize="sm" fontWeight="semibold">
        {title}
      </Text>
      <Text fontSize="sm" color="muted" mt={1}>
        {children}
      </Text>
      <HStack spacing={4} mt={3} flexWrap="wrap">
        <Button size="sm" colorScheme="brand" onClick={handleFix}>
          {fixLabel}
        </Button>
        <Button size="sm" variant="link" onClick={handleKeep}>
          {keepLabel}
        </Button>
      </HStack>
    </Box>
  );
}
